import React, { useEffect, useState } from 'react';
import styled, { css } from 'styled-components';
import SVG from 'react-inlinesvg';
import {
  Icon,
  isDarkThemeMixin,
  sparkleSolid,
  Switch,
  TextButton,
} from '@amzn/storm-ui';
import { useSelector, useDispatch } from 'react-redux';
import { selectEmbeddedChatOpen, setEmbeddedChatOpen } from 'src/app/slice';
import { setEmbeddedChatVisible } from 'src/commons/utils/a3cEmbeddedChat';
import { isStormConfigFeatureEnabled } from 'src/commons/utils/stormConfig';
import { WeblabedFeatures } from 'src/commons/constants';
import { useSidenavBundle } from 'src/providers/PantherLocalizationProvider';
import { HiddenContent } from './styles';

export const ADS_AGENT_OPEN_ICON_PATH =
  'https://m.media-amazon.com/images/G/01/CCX/svg/A3C_nav_button_Open.svg';
export const ADS_AGENT_CLOSE_ICON_PATH =
  'https://m.media-amazon.com/images/G/01/CCX/svg/A3C_nav_button_Closed.svg';

export const NOTIFICATION_BADGE_EVENT = 'A3C_NOTIFICATION_BADGE_UPDATED';

// Resting state of the rebranded circle, read by the row's hover rule.
export const ACTIVE_STATE_ATTR = 'data-a3c-active';

// Figma "A3C Nav Icon" (Storm Community Library node 15977:10981), identical
// across all six states; also the written requirement on P500910840.
const REBRAND_CIRCLE_SIZE = '32px';
const REBRAND_GLYPH_SIZE = '18px';
// Half the circle's growth over the 24px box every other rail item uses, so it
// stays centred on the shared icon column instead of only sharing its left edge.
const REBRAND_CIRCLE_INSET = '4px';
// The collapsed rail's inner width: 64px rail less its 12px padding either side.
const REBRAND_DIVIDER_WIDTH = '40px';

export const AdsAgentButtonWrapper = styled.div<{ isNavOpen: boolean }>`
  min-height: 40px;
  width: 296px;
  position: relative;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  flex-shrink: 0;
`;

export const AdsAgentDivider = styled.hr<{
  isNavOpen: boolean;
  isRebrand: boolean;
}>`
  border: none;
  border-top: 2px solid ${({ theme }) => theme.border.color.subtle.default};
  margin: 10px 0px;
  margin-inline: ${({ isNavOpen, isRebrand }) =>
    isRebrand && !isNavOpen ? '0px' : '4px'};
  flex-shrink: 0;
  width: ${({ isNavOpen, isRebrand }) => {
    if (isNavOpen) return 'calc(100% - 8px)';
    return isRebrand ? REBRAND_DIVIDER_WIDTH : '32px';
  }};
`;

export const AdsAgentIconWrapper = styled.div<{
  isOpen: boolean;
  isRebrand: boolean;
}>`
  position: relative;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  margin-inline-end: ${({ theme }) => theme.spacing.small.default};

  ${({ isRebrand, isOpen, theme }) =>
    isRebrand
      ? css`
          box-sizing: border-box;
          width: ${REBRAND_CIRCLE_SIZE};
          height: ${REBRAND_CIRCLE_SIZE};
          margin-inline-start: -${REBRAND_CIRCLE_INSET};
          margin-inline-end: calc(
            ${theme.spacing.small.default} - ${REBRAND_CIRCLE_INSET}
          );
          border-radius: ${theme.border.radius.round};
          border: 2px solid
            ${isOpen ? 'transparent' : theme.border.color.subtle.default};
          background-color: ${isOpen
            ? theme.support.borderColor.ai
            : theme.surface.default};
          color: ${isOpen
            ? theme.icon.color.inverted
            : theme.icon.color.default};
          transition:
            background-color 100ms linear,
            border-color 100ms linear,
            color 100ms linear;

          @media screen and (prefers-reduced-motion: reduce) {
            & {
              transition: none !important;
            }
          }

          & svg {
            width: ${REBRAND_GLYPH_SIZE};
            height: ${REBRAND_GLYPH_SIZE};
          }
        `
      : css`
          & svg path {
            ${isDarkThemeMixin(css`
              fill: ${isOpen ? '#B07CFF' : '#F9F8F6'};
            `)};
          }
        `}
`;

export const StyledSwitchWrapper = styled.div`
  position: absolute;
  inset-inline-end: ${({ theme }) => theme.spacing.xsmall.default};
  top: 20px;
  transform: translateY(-50%);
  pointer-events: none;
`;

export const NotificationDot = styled.span`
  position: absolute;
  top: -2px;
  inset-inline-end: -2px;
  width: 8px;
  height: 8px;
  border-radius: ${({ theme }) => theme.border.radius.round};
  background-color: ${({ theme }) => theme.color.red[700]};
  pointer-events: none;
`;

export const AdsAgentButtonInner = styled(TextButton)<{
  isNavOpen: boolean;
  isRebrand: boolean;
}>`
  box-sizing: border-box;
  width: 100%;
  display: flex;
  align-items: center;
  border: none;
  background-color: ${({ theme }) =>
    theme.selectableContainer.backgroundColor.default};
  min-height: 40px;
  padding-inline: ${({ theme }) =>
    `${theme.spacing.xxsmall.default} ${theme.spacing.xsmall.default}`};
  border-radius: ${({ theme }) => theme.border.radius.large};
  color: ${({ theme }) => theme.text.color.default};
  font: inherit;

  &&& {
    text-decoration: none;
    color: ${({ theme }) => theme.text.color.default};
  }

  ${({ isRebrand, theme }) =>
    isRebrand &&
    css`
      &:hover ${AdsAgentIconWrapper}[${ACTIVE_STATE_ATTR}='false'],
      &:focus-visible ${AdsAgentIconWrapper}[${ACTIVE_STATE_ATTR}='false'] {
        border-color: ${theme.support.borderColor.ai};
        color: ${theme.support.iconColor.ai};
      }

      &:hover + ${StyledSwitchWrapper} [role='switch'][aria-checked='false'],
      &:focus-visible
        + ${StyledSwitchWrapper}
        [role='switch'][aria-checked='false'] {
        background-color: ${theme.selectableWidget.backgroundColor.selected};
      }
    `}
`;

interface AdsAgentNavButtonProps {
  isNavOpen: boolean;
}

const AdsAgentNavButton: React.VFC<AdsAgentNavButtonProps> = ({
  isNavOpen,
}) => {
  const dispatch = useDispatch();
  const isOpen = useSelector(selectEmbeddedChatOpen);
  const bundle = useSidenavBundle();
  const [hasPendingNotifications, setHasPendingNotifications] = useState(false);

  useEffect(() => {
    const handler = (event: Event) => {
      const hasPending =
        (event as CustomEvent<{ hasPending: boolean }>).detail?.hasPending ??
        false;
      setHasPendingNotifications(hasPending);
    };
    document.addEventListener(NOTIFICATION_BADGE_EVENT, handler);
    return () => {
      document.removeEventListener(NOTIFICATION_BADGE_EVENT, handler);
    };
  }, []);

  // eslint-disable-next-line @typescript-eslint/no-empty-function
  const noop = () => {};

  const handleClick = () => {
    const nextState = !isOpen;
    dispatch(setEmbeddedChatOpen(nextState));
    setEmbeddedChatVisible(nextState);
  };

  const iconPath = isOpen
    ? ADS_AGENT_OPEN_ICON_PATH
    : ADS_AGENT_CLOSE_ICON_PATH;

  const isRebrand = isStormConfigFeatureEnabled(
    WeblabedFeatures.ADS_AI_REBRAND,
  );

  const label = bundle.getMessage('sidenav-ads-agent-label');
  const switchLabel = bundle.getMessage('sidenav-ads-agent-switch-label');
  const showBadge = hasPendingNotifications && !isOpen;
  const ariaLabel = showBadge
    ? bundle.formatMessage('sidenav-ads-agent-notification-label', { label }) ||
      label
    : label;

  return (
    <AdsAgentButtonWrapper isNavOpen={isNavOpen}>
      <AdsAgentButtonInner
        isNavOpen={isNavOpen}
        isRebrand={isRebrand}
        data-ccx-e2e-id="ads-agent-nav-button"
        onClick={handleClick}
        aria-label={ariaLabel}
        aria-pressed={isOpen}
      >
        <AdsAgentIconWrapper
          isOpen={isOpen}
          isRebrand={isRebrand}
          {...{ [ACTIVE_STATE_ATTR]: isOpen ? 'true' : 'false' }}
        >
          {isRebrand ? (
            <Icon data-testid="ads-agent-nav-glyph" icon={sparkleSolid} />
          ) : (
            <SVG src={iconPath} width={24} height={24} />
          )}
          {showBadge && (
            <NotificationDot
              data-testid="notification-badge"
              aria-hidden="true"
            />
          )}
        </AdsAgentIconWrapper>
        <HiddenContent alignItems="center" justifyContent="space-between">
          {label}
        </HiddenContent>
      </AdsAgentButtonInner>
      <StyledSwitchWrapper
        aria-hidden="true"
        ref={(el: HTMLDivElement | null) => {
          if (el) (el as any).inert = true;
        }}
      >
        <Switch
          id="ads-agent-toggle"
          toggled={isOpen}
          onChange={noop}
          small
          switchLabel={switchLabel}
        />
      </StyledSwitchWrapper>
      <AdsAgentDivider
        isNavOpen={isNavOpen}
        isRebrand={isRebrand}
        role="separator"
      />
    </AdsAgentButtonWrapper>
  );
};

export default AdsAgentNavButton;
